import React, { useEffect, useState } from "react";
import { DialogContent, DialogContentText, Grid, Typography } from "@mui/material";

import { useSelector } from "react-redux";
import Popup from "../../../../components/common/model/Popup";
import closeIcon from "../../../../assets/closeModel.png";
import checkedIcon from "../../../../assets/check-mark.png";
import deleteIcon from "../../../../assets/trash.png";
import useStyles from "../../../../components/common/model/styles";
import FirstTabSection from "./FirstTabSection";
import CancellationFees from "./CancellationFees";
import InvoiceTypes from "./InvoiceTypes";
import PaymentMethod from "./PaymentMethod";
import { useDispatch } from "react-redux";
import {
  intialFinancialState,
  postFinancialAction,
  setPostFinancialResponse,
} from "../../../../store/reducers/quickBookSlice";
import Notification from "../../../../components/common/notification/Notification";
import { setNotificationAction } from "../../../../store/reducers/mmymSlice";
import AlertPopup from "../../../../components/common/alertPopup/AlertPopup";
import Validation from "../../../../utils/Validation";

interface Props {
  open: boolean; // Define the 'open' prop
  onHide: () => void; // Define the 'onHide' prop
  title: string; // Define the 'title' prop
  selectedLead?: any; // Adjust type as per your requirement
  selectedInvoice?: any; // Adjust type as per your requirement
  invoiceType?: any;
  isFormReadyOnly?: any;
  isNewInvoice?: boolean; // Optional prop to indicate if it's a new invoice
  isSetLeadDetails?: boolean; // Optional prop to indicate if lead details should be set
}

const ManageFinancialForm = (props: Props) => {
  const classes = useStyles();
  const { onHide, open, title } = props;
  const dispatch = useDispatch();
  const { newFinancialState, isPostFinancial } = useSelector(
    (state: any) => state?.quickBook
  );
  const { userProfile } = useSelector((state: any) => state?.user);
  const [isProfessionalSection, setIsProfessionalSection] = useState(
    userProfile?.features?.includes("Professional Financial") || false // Safe fallback
  );

  useEffect(() => {
    setIsProfessionalSection(
      userProfile?.features?.includes("Professional Financial") || false
    ); // Sync profile changes
  }, [userProfile]);

  const [resetAlert, setResetAlert] = useState(false);
  const [alertModel, setAlertModel] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [isChanged, setIsChanged] = useState(false);
  const [isRegNo, setIsRegNo] = useState(false);
  const [isActionCalled, setIsActionCalled] = useState(false);
  const [formData, setFormData] = useState<any>(
    newFinancialState ? newFinancialState : intialFinancialState
  );
  const [invoiceNameErrors, setInvoiceNameErrors] = useState<string[] | null>(null);
  const [paymentNameErrors, setPaymentNameErrors] = useState<string[] | null>(null);

  useEffect(() => {
    dispatch(setNotificationAction({ message: [], severity: "error" }));
  }, []);

  useEffect(() => {
    if (isPostFinancial) {
      setIsChanged(false);
      setIsActionCalled(false);
      dispatch(setPostFinancialResponse(false));
    }
  }, [isPostFinancial]);

  const validationData: any = {
    invoice_address: formData?.invoice_address || "",
    payment_terms: formData?.payment_terms || "",
    ...(isRegNo
      ? {
          vat_percent: formData?.vat_percent || "",
          vat_reg_no: formData?.vat_reg_no || "",
        }
      : ""),
  };

  const [
    error,
    checkValidator,
    resetValidator,
    resetAllValidator,
    isError,
  ]: any = Validation(validationData);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    checkValidator();
    setIsActionCalled(true);
  };

  useEffect(() => {
    if (isActionCalled) {
      if (!isError() && error !== null && isActionCalled) {
        // row-level validation for invoice types
        const invoiceList = formData?.invoice_types || [];
        const invoiceErrors = invoiceList.map((r: any) =>
          r?.name && String(r.name).trim() !== "" ? null : "Name is required"
        );

        // row-level validation for payment methods (name required)
        const paymentList = formData?.payment_methods || [];
        const paymentErrors = paymentList.map((r: any) =>
          r?.name && String(r.name).trim() !== "" ? null : "Name is required"
        );

        const hasInvoiceErrors = invoiceErrors.some((e: any) => e !== null);
        const hasPaymentErrors = paymentErrors.some((e: any) => e !== null);

        if (hasInvoiceErrors || hasPaymentErrors) {
          setInvoiceNameErrors(invoiceErrors);
          setPaymentNameErrors(paymentErrors);
        } else {
          // clear any previous row errors
          setInvoiceNameErrors(null);
          setPaymentNameErrors(null);

          const updatedFormData = {
            ...formData,
            cancellation_fees: formData?.cancellation_fees?.map((item: any) => ({
              days: item?.days ? Number(item.days) : 0,
              percent: item?.percent ? Number(item.percent) : 0,
            })),
            vat_percent: formData?.vat_percent ? Number(formData.vat_percent) : 0,
            deposit_payment_percent: formData?.deposit_payment_percent
              ? Number(formData.deposit_payment_percent)
              : 0,
            invoice_due_days: formData?.invoice_due_days
              ? Number(formData.invoice_due_days)
              : 0,
          };

          dispatch(postFinancialAction(updatedFormData));
        }
      }
    }
    setIsActionCalled(false);
  }, [isActionCalled]);

  useEffect(() => {
    const vatPercent = newFinancialState?.vat_percent;
    const vatRegNo = newFinancialState?.vat_reg_no;

    setIsRegNo(!!vatPercent && !!vatRegNo);
  }, [newFinancialState]);


  const hideHandler = () => {
    if (isChanged) {
      setAlertModel(true);
      setAlertMessage(
        "Changes have been made. Are you sure you abandon the changes?"
      );
    } else {
      onHide();
    }
  };

  const handleClear = () => {
    setAlertModel(true);
    setResetAlert(true);
    setAlertMessage("Are you sure you want to reset the form?");
  };

  const resetFormData = () => {
    if (newFinancialState?.id) {
      setFormData(newFinancialState);
    } else {
      setFormData(intialFinancialState);
    }
    setIsChanged(false);
    setResetAlert(false);
    setAlertModel(false);
    resetAllValidator();
    setIsActionCalled(false);
    setInvoiceNameErrors(null);
    setPaymentNameErrors(null);
  };

  const closeAlertModel = () => {
    setAlertModel(false);
    setResetAlert(false);
    onHide();
  };

  // Clear row errors when user fixes rows
  useEffect(() => {
    if (invoiceNameErrors) {
      const invoiceList = formData?.invoice_types || [];
      const invoiceErrors = invoiceList.map((r: any) =>
        r?.name && String(r.name).trim() !== "" ? null : "Name is required"
      );
      if (!invoiceErrors.some((e: any) => e !== null)) setInvoiceNameErrors(null);
    }
  }, [formData?.invoice_types]);

  useEffect(() => {
    if (paymentNameErrors) {
      const paymentList = formData?.payment_methods || [];
      const paymentErrors = paymentList.map((r: any) =>
        r?.name && String(r.name).trim() !== "" ? null : "Name is required"
      );
      if (!paymentErrors.some((e: any) => e !== null)) setPaymentNameErrors(null);
    }
  }, [formData?.payment_methods]);

  

  return (
    <>
      <Popup
        open={open} // Example value, replace with your actual state or variable
        title={title} // Example title, replace with your actual title
        onHide={hideHandler}
        fullWidth={true}
        maxWidth={"lg"}
        aria-describedby="alert-dialog-slide-description"
        popupBg="#FFFFFF"
        header={
          <>
            <img
              onClick={(e: any) => handleSubmit(e)}
              src={checkedIcon}
              className={classes.checkicons}
              alt="save"
            />
            <img
              onClick={handleClear}
              src={deleteIcon}
              className={classes.deleteicons}
              alt="delete"
            />
            <img
              onClick={hideHandler}
              src={closeIcon}
              className={classes.closeicons}
              alt="close"
            />
          </>
        }
      >
        <DialogContent sx={{ padding: "10px" }}>
          <DialogContentText>
            <>
              <Notification />
              <Grid
                container
                spacing={2}
                sx={{
                  padding: "10px",
                  mt: 1,
                  background: "#E5F4F1",
                  borderRadius: "5px",
                  border: "1px solid #CFCFCF",
                }}
              >
                <FirstTabSection
                  formData={formData}
                  setFormData={setFormData}
                  setIsChanged={setIsChanged}
                  isRegNo={isRegNo}
                  setIsRegNo={setIsRegNo}
                  error={error}
                  checkValidator={checkValidator}
                />
                {isProfessionalSection && (
                  <CancellationFees
                    formData={formData}
                    setFormData={setFormData}
                    setIsChanged={setIsChanged}
                  />
                )}
                {isProfessionalSection && (
                  <InvoiceTypes
                    formData={formData}
                    setFormData={setFormData}
                    setIsChanged={setIsChanged}
                    nameErrors={invoiceNameErrors}
                  />
                )}
                {isProfessionalSection && (
                  <PaymentMethod
                    formData={formData}
                    setFormData={setFormData}
                    setIsChanged={setIsChanged}
                    nameErrors={paymentNameErrors}
                  />
                )}
                <Grid
                  item
                  md={12}
                  xs={12}
                  display={"flex"}
                  justifyContent={"flex-end"}
                >
                  {/* <CustomButton
                
                    style={{ width: "120px" }}
                    title={"Save"}
                    onClick={handleSubmit}
                  /> */}
                </Grid>
              </Grid>
            </>
          </DialogContentText>
        </DialogContent>
      </Popup>
      {alertMessage && (
        <AlertPopup
          open={alertModel}
          // onClose={() => setAlertModel(false)}
          title="Alert"
          content={alertMessage}
          submitHandler={() => {
            resetAlert ? resetFormData() : closeAlertModel();
          }}
          onHide={() => setAlertModel(false)}
          isError={false}
        />
      )}
    </>
  );
};

export default ManageFinancialForm;
